import React, { useCallback, useContext } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "../../../index";
import { useLogViewer } from "./useLogViewer";
import { LogViewerPanel } from "./LogViewerPanel";
import { NewProtocolCommandPanel } from "./components/NewProtocolCommandPanel";
import "./styles.css";

type LogsViewerVariant = "legacy" | "new-protocol";

interface LogsViewerProps {
  variant?: LogsViewerVariant;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface LogsViewerConfig {
  title: string;
  downloadFileName: string;
  isNewProtocol: boolean;
  /** DEV-88 (`OPS-20` (в)): подпись под заголовком — что показывает страница. */
  note?: string;
}

const CONFIG: Record<LogsViewerVariant, LogsViewerConfig> = {
  legacy: {
    title: "траффик между сервером и МДБН",
    downloadFileName: "messages.log",
    isNewProtocol: false,
  },
  "new-protocol": {
    title: "траффик между сервером и МДБН (новый протокол)",
    downloadFileName: "messages-new-protocol.log",
    isNewProtocol: true,
    // `OPS-20` (в): нового протокола у МДБН нет — кадры приходят только от
    // ММБ/СМБ, а журнал МДБН лежит в обычных логах (`Logs`), рядом с этим
    // заголовком это иначе не видно.
    note:
      "только ММБ/СМБ; журнал МДБН — в таблице Logs (страница обычных логов)",
  },
};

const LogsViewer = ({
  variant = "legacy",
  autoRefresh = true,
  refreshInterval = 5000,
}: LogsViewerProps) => {
  const { adminStore } = useContext(Context);
  const config = CONFIG[variant];

  const fetchLogs = useCallback(
    () => adminStore.fetchLogs(config.isNewProtocol),
    [adminStore, config.isNewProtocol],
  );

  // `В4` (условие слияния `F-454`): страницы трафика журнал **не чистят**.
  // Прежняя кнопка звала `DELETE /logs` и стирала `app.log` бэкенда — то есть
  // технический журнал сервера, а не файл шлюза, который человек думал, что
  // чистит. Очистка одна и живёт на `/admin/devlog` (`POST /admin/logs/clear`
  // со следом `system_log.cleared`); прокси к журналу шлюза — отдельная задача.
  const { logs, loading, error } = useLogViewer(fetchLogs, {
    autoRefresh,
    refreshInterval,
  });

  return (
    <LogViewerPanel
      title={config.title}
      logs={logs}
      loading={loading}
      error={error}
      downloadFileName={config.downloadFileName}
      note={config.note}
    >
      {variant === "new-protocol" && <NewProtocolCommandPanel />}
    </LogViewerPanel>
  );
};

export default observer(LogsViewer);
